import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, InputNumber, Progress, Space, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { guardrailsApi, type BudgetMeter } from '../guardrails.api';

function Meter({ label, unit, meter }: { label: string; unit: string; meter: BudgetMeter }) {
  const pct =
    meter.cap && meter.cap > 0 ? Math.min(100, Math.round((meter.used / meter.cap) * 100)) : 0;
  return (
    <div style={{ minWidth: 220 }}>
      <Space>
        <Typography.Text strong>{label}</Typography.Text>
        {meter.exceeded ? <Tag color="red">Budget reached</Tag> : null}
      </Space>
      <div style={{ color: '#6b7280', fontSize: 12 }}>
        {meter.used.toLocaleString()} {unit} used
        {meter.cap === null ? ' · no cap' : ` of ${meter.cap.toLocaleString()} ${unit}`}
      </div>
      {meter.cap !== null ? (
        <Progress percent={pct} status={meter.exceeded ? 'exception' : 'active'} size="small" />
      ) : null}
    </div>
  );
}

/**
 * Shows this project's monthly AI-token and ad-spend usage against their caps,
 * and (for admins) lets them set the caps. When a cap is reached the platform
 * blocks further AI calls / ad activations until it's raised.
 */
export function GuardrailsCard({
  orgId,
  projectId,
  canEdit,
}: {
  orgId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const key = ['guardrails', orgId, projectId] as const;
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => guardrailsApi.get(orgId, projectId),
  });

  const [tokenCap, setTokenCap] = useState<number | null>(null);
  const [spendCap, setSpendCap] = useState<number | null>(null);
  useEffect(() => {
    if (data) {
      setTokenCap(data.aiTokens.cap);
      setSpendCap(data.adSpend.cap);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      guardrailsApi.update(orgId, projectId, {
        monthlyAiTokenCap: tokenCap,
        monthlyAdSpendCap: spendCap,
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(key, res);
      message.success('Guardrails updated');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to update guardrails'),
  });

  return (
    <Card
      size="small"
      title="Budgets & guardrails (this month)"
      loading={isLoading}
      extra={
        canEdit ? (
          <Button
            type="primary"
            size="small"
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save caps
          </Button>
        ) : null
      }
    >
      {data ? (
        <Space size="large" wrap align="start">
          <Meter label="AI tokens" unit="tokens" meter={data.aiTokens} />
          <Meter label="Ad spend" unit={data.currency} meter={data.adSpend} />
          {canEdit ? (
            <Space direction="vertical" size={4}>
              <Space>
                <span style={{ width: 120, display: 'inline-block' }}>AI token cap</span>
                <InputNumber
                  min={0}
                  value={tokenCap}
                  placeholder="unlimited"
                  onChange={(v) => setTokenCap(typeof v === 'number' ? v : null)}
                />
              </Space>
              <Space>
                <span style={{ width: 120, display: 'inline-block' }}>Ad spend cap</span>
                <InputNumber
                  min={0}
                  value={spendCap}
                  placeholder="unlimited"
                  prefix={data.currency}
                  onChange={(v) => setSpendCap(typeof v === 'number' ? v : null)}
                />
              </Space>
            </Space>
          ) : null}
        </Space>
      ) : null}
    </Card>
  );
}
